import React, { useEffect, useState } from "react";
import { CmCollection, Cmhtml, CmVideo } from "../../../../packages/graphql-layer/src";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import C041Video from "../C041Video/C041Video";
import CCOPicture from "../../fundamentals/CCOPicture";
import CCOContainer from "../../fundamentals/CCOContainer";
import CCOAccordion from "../../fundamentals/CCOAccordion";
import DownloadItems from "../C003Download/DownloadItems";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import C067HtmlFragment from "../C067HtmlFragment/C067HtmlFragment";
import { RichTextElement } from "../../fundamentals/CCORichText/CCORichTextHelper";
import { getContainerWidth } from "../../../utility/componentUtil";


type Props = {
    cmCollection: CmCollection;
    headline?: string;
    layoutSettings?: { contentWidth?: string };
};

const TRANSITION_TIMEOUT_DURATION_MS = 1000;

const C001Accordion: React.FC<Props> = ({ cmCollection, headline, layoutSettings }) => {
    const getHash = () => {if (typeof window !== "undefined") return window.location.hash.replace("#", "")};

    const firstPanelId = cmCollection.items?.length
        ? `panel${cmCollection.id}${(cmCollection as any).items[0].id}`
        : "";

    const initialExpandedPanel = (() => {
        const hashPanelId = `panel${cmCollection.id}${getHash()}`;
        if (typeof window !== "undefined" &&  window.location.hash && document.getElementById(hashPanelId)) {
            return hashPanelId;
        } else if ((cmCollection as any).accordionSettings?.accordionOpen) {
            return firstPanelId;
        } else {
            return "";
        }
    })();

    const [currentlyExpandedPanel, setCurrentlyExpandedPanel] = useState(initialExpandedPanel);
    const containerWidth = getContainerWidth(layoutSettings?.contentWidth);

    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setCurrentlyExpandedPanel(isExpanded ? panel : "");
    };

    useEffect(() => {
        const handleHashChange = () => {
            const panelId = `panel${cmCollection.id}${getHash()}`;
            if (document.getElementById(panelId)) {
                setCurrentlyExpandedPanel(panelId);
            }
        };

        handleHashChange();
        window.addEventListener("hashchange", handleHashChange);

        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, [cmCollection.id]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        // when the height of the currently opened panel changes,
        // the panel scrolls into view if it's not already visible
        const handleTransitionEnd = (event: TransitionEvent) => {
            if (currentlyExpandedPanel && event.propertyName === "height") {
                const panelElement = document.getElementById(currentlyExpandedPanel);
                const panelRect = panelElement?.getBoundingClientRect();
                const panelTop = panelRect?.top || 0;

                if (panelElement && panelTop < 0) {
                    panelElement.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }
        };

        const clearTransitionTimeout = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        document.addEventListener("transitionend", handleTransitionEnd);


        // the event listener is cleared after 1 second; this is done to ensure that only the last panel
        // opened is set as the scroll target (and not another panel that is still open in another accordion)
        timeoutId = setTimeout(() => {
            document.removeEventListener("transitionend", handleTransitionEnd);
            clearTransitionTimeout();
        }, TRANSITION_TIMEOUT_DURATION_MS);

        return () => {
            document.removeEventListener("transitionend", handleTransitionEnd);
            clearTransitionTimeout();
        };
    }, [currentlyExpandedPanel]);

    return (
        <CCOComponentWrapper dataCypress={"accordionComponent"}>
            <CCOHeadline
                headline={headline ? headline : cmCollection.teaserTitle || "" }
                propertyName={headline ? "properties.headline" : "properties.teaserTitle"}
                id={cmCollection.id}
                type="componenthead"
                subtext={cmCollection?.teaserText?.textAsTree}
            />
            <CCOContainer width={containerWidth}>
                {cmCollection.items?.map((item: any, index: number) => {
                    const richText = item.detailText ? item.detailText : item.teaserText;
                    const panelId = `panel${cmCollection.id}${item.id}`;
                    return (
                        <div key={index} id={panelId}>
                            <CCOAccordion
                                id={item.id}
                                key={index}
                                title={item.title || item.teaserTitle || ""}
                                expanded={currentlyExpandedPanel === panelId}
                                onChange={handleChange(panelId)}
                            >
                                <>
                                    {richText ? (
                                        <CCORichText metadataId={item.id} textAsTree={richText.textAsTree as RichTextElement} />
                                    ) : item.html ? (
                                        <C067HtmlFragment
                                            cmHtml={{ html: item.html, activeElement: item.activeElement } as Cmhtml}
                                        />
                                    ) : null}
                                    {item.media?.map((media: any, mediaIndex: number) => {
                                        return media.__typename === "CMVideoImpl" ? (
                                            <C041Video key={mediaIndex} video={{ dataUrl: media.dataUrl } as CmVideo} noContainer />
                                        ) : media.__typename === "CMPictureImpl" ? (
                                            <CCOPicture
                                                key={mediaIndex}
                                                name={media.name}
                                                picture={media}
                                                width="540"
                                                height="540"
                                                orientation="portrait_ratio1x1"
                                            />
                                        ) : null;
                                    })}
                                    {item.items && item.items.length > 0 ? (
                                        item.items[0].type === "CMDownload" ? (
                                            <DownloadItems cmCollection={{ items: item.items } as CmCollection} hideBottomDivider />
                                        ) : null
                                    ) : null}
                                </>
                            </CCOAccordion>
                        </div>
                    );
                })}
            </CCOContainer>
        </CCOComponentWrapper>
    );
};

export default C001Accordion;
